import React from 'react';
import { motion } from 'framer-motion';
import { Settings2, Users, Calendar, BarChart3, AlertTriangle, CheckCircle } from 'lucide-react';

const ControlBridgeWorkspace: React.FC = () => {
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="p-3 border-b border-slate-700/50 flex-shrink-0">
        <h3 className="text-lg font-medium violett-gradient-text text-drop-shadow">Control Bridge</h3>
        <p className="text-sm text-slate-400 mt-1">Project Management &amp; Workflow Operations</p>
      </div>
      
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          <motion.div 
            className="glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Settings2 className="w-6 h-6 text-blue-400" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Active Projects</h4>
            </div>
            <div className="space-y-3">
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Beehive Core v2.0</span>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">On Track</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">Agentic architecture implementation and UI overhaul</p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div className="bg-gradient-to-r from-green-400 to-emerald-500 h-2 rounded-full" style={{ width: '78%' }}></div>
                  </div>
                  <span className="text-xs text-slate-400">78%</span>
                </div>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">DebugBee Integration</span>
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">In Progress</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">Proactive monitoring and anomaly detection system</p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div className="bg-gradient-to-r from-blue-400 to-purple-500 h-2 rounded-full" style={{ width: '92%' }}></div>
                  </div>
                  <span className="text-xs text-slate-400">92%</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-purple-400" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Team Status</h4>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-xs font-bold">
                    DA
                  </div>
                  <div>
                    <div className="font-medium">Dana Anderson</div>
                    <div className="text-xs text-slate-400">Lead Developer</div>
                  </div>
                </div>
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-xs font-bold">
                    AI
                  </div>
                  <div>
                    <div className="font-medium">Devin AI</div>
                    <div className="text-xs text-slate-400">Development Agent</div>
                  </div>
                </div>
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-xs font-bold">
                    SB
                  </div>
                  <div>
                    <div className="font-medium">SupervisorBee</div>
                    <div className="text-xs text-slate-400">System Orchestrator</div>
                  </div>
                </div>
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="w-6 h-6 text-cyan-400" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Upcoming Milestones</h4>
            </div>
            <div className="space-y-3">
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Workspace UI Launch</span>
                  <span className="text-xs text-slate-400">Jan 25</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">Complete workspace program switching implementation</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">AgentCommunicationBus</span>
                  <span className="text-xs text-slate-400">Jan 28</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">Replace all WebSocket dependencies with universal event system</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Production Deployment</span>
                  <span className="text-xs text-slate-400">Feb 1</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">Full system deployment with monitoring and alerts</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <BarChart3 className="w-6 h-6 text-green-400" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Performance Metrics</h4>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Sprint Completion</span>
                  <span className="text-sm font-medium text-green-400">89%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div className="bg-gradient-to-r from-green-400 to-emerald-500 h-2 rounded-full" style={{ width: '89%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Code Quality Score</span>
                  <span className="text-sm font-medium text-blue-400">94/100</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div className="bg-gradient-to-r from-blue-400 to-purple-500 h-2 rounded-full" style={{ width: '94%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Test Coverage</span>
                  <span className="text-sm font-medium text-cyan-400">87%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div className="bg-gradient-to-r from-cyan-400 to-blue-500 h-2 rounded-full" style={{ width: '87%' }}></div>
                </div>
              </div>
            </div>
          </motion.div>

        </div>

        <motion.div 
          className="mt-6 glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-orange-400" />
            <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Recent Alerts</h4>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-sm">DebugBee successfully deployed and monitoring system health</span>
              <span className="text-xs text-slate-400 ml-auto">2 min ago</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              <span className="text-sm">WebSocket connection fallback activated for EditorBee</span>
              <span className="text-xs text-slate-400 ml-auto">8 min ago</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-sm">Workspace UI components successfully integrated</span>
              <span className="text-xs text-slate-400 ml-auto">15 min ago</span>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default ControlBridgeWorkspace;
